import { useMemo } from 'react'
import { useMantineTheme } from '@mantine/core'

type Hsl = { h: number; s: number; l: number }

const hexToHsl = (hex: string): Hsl => {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) {
    return { h: 0, s: 0, l }
  }
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) * 60
      break
    case g:
      h = ((b - r) / d + 2) * 60
      break
    default:
      h = ((r - g) / d + 4) * 60
  }
  return { h, s, l }
}

const hslToHex = ({ h, s, l }: Hsl): string => {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const hp = h / 60
  const x = c * (1 - Math.abs((hp % 2) - 1))
  let r1 = 0
  let g1 = 0
  let b1 = 0
  if (hp >= 0 && hp < 1) [r1, g1, b1] = [c, x, 0]
  else if (hp < 2) [r1, g1, b1] = [x, c, 0]
  else if (hp < 3) [r1, g1, b1] = [0, c, x]
  else if (hp < 4) [r1, g1, b1] = [0, x, c]
  else if (hp < 5) [r1, g1, b1] = [x, 0, c]
  else if (hp < 6) [r1, g1, b1] = [c, 0, x]
  const m = l - c / 2
  const toByte = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${toByte(r1)}${toByte(g1)}${toByte(b1)}`
}

// Interpolate hue along the shorter arc so yellow→green doesn't wrap through magenta.
const interpolateHue = (a: number, b: number, t: number): number => {
  const diff = ((b - a + 540) % 360) - 180
  return (a + diff * t + 360) % 360
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t

export const interpolateHslPalette = (stops: string[], n: number): string[] => {
  if (n <= 0) return []
  if (stops.length === 0) return Array(n).fill('#808080')
  if (stops.length === 1 || n === 1) return Array(n).fill(stops[0])
  const hslStops = stops.map(hexToHsl)
  const segments = hslStops.length - 1
  return Array.from({ length: n }, (_, i) => {
    const pos = (i / (n - 1)) * segments
    const idx = Math.min(Math.floor(pos), segments - 1)
    const t = pos - idx
    const a = hslStops[idx]
    const b = hslStops[idx + 1]
    return hslToHex({
      h: interpolateHue(a.h, b.h, t),
      s: lerp(a.s, b.s, t),
      l: lerp(a.l, b.l, t),
    })
  })
}

export const useVehicleColors = (vehicleIds: string[]): Record<string, string> => {
  const theme = useMantineTheme()
  return useMemo(() => {
    const sorted = [...vehicleIds].sort()
    const stops = [theme.colors.yellow[9], theme.colors.blue[9], theme.colors.red[9], theme.colors.green[9]]
    const palette = interpolateHslPalette(stops, sorted.length)
    return Object.fromEntries(sorted.map((id, i) => [id, palette[i]]))
  }, [vehicleIds, theme])
}
